import { z } from 'zod';
import { ZodParseError } from 'libs/end-to-end/src/types/errors/zod-parse-error';
import { UnknownError } from 'libs/end-to-end/src/types/errors/unknown-error';
import { GetAllDatabasesAndRevisionsError } from 'libs/end-to-end/src/types/errors/get-all-databases-and-revisions-error';
import { MergeErrorFromLibrary } from 'libs/end-to-end/src/types/errors/merge-error-from-library';
import { BaseErrorSchema } from 'libs/end-to-end/src/types/errors/base-error-schema';
import { UserError } from 'libs/end-to-end/src/types/errors/user-error';
import { OrganizationError } from 'libs/end-to-end/src/types/errors/organization-error';

export const CustomVizRestrictedError = BaseErrorSchema.extend({
  errorType: z.literal('CustomVizRestrictedError'),
  organizationId: z.string(),
  customVizId: z.string(),
});

export type CustomVizRestrictedError = z.infer<typeof CustomVizRestrictedError>;

export const NotAliveError = BaseErrorSchema.extend({
  errorType: z.literal('NotAliveError'),
});

export type NotAliveError = z.infer<typeof NotAliveError>;

export const SimpleError = BaseErrorSchema.extend({
  errorType: z.literal('SimpleError'),
  errorReason: z.string(),
});

export type SimpleError = z.infer<typeof SimpleError>;

export const SsoNotEnabledError = BaseErrorSchema.extend({
  errorType: z.literal('SsoNotEnabledError'),
});

export type SsoNotEnabledError = z.infer<typeof SsoNotEnabledError>;

export const DatabaseNotFoundError = BaseErrorSchema.extend({
  errorType: z.literal('DatabaseNotFoundError'),
  id: z.string(),
});

export type DatabaseNotFoundError = z.infer<typeof DatabaseNotFoundError>;

export const DatabaseSchemaNotFoundError = BaseErrorSchema.extend({
  errorType: z.literal('DatabaseSchemaNotFoundError'),
  id: z.string(),
});

export type DatabaseSchemaNotFoundError = z.infer<
  typeof DatabaseSchemaNotFoundError
>;

export const GetMeOrganizationsDatabasesAndRevisionsError =
  GetAllDatabasesAndRevisionsError.or(UserError).or(OrganizationError);

export type GetMeOrganizationsDatabasesAndRevisionsError = z.infer<
  typeof GetMeOrganizationsDatabasesAndRevisionsError
>;

export const DatabaseScheduleNotFoundError = BaseErrorSchema.extend({
  errorType: z.literal('DatabaseScheduleNotFoundError'),
  organizationId: z.string(),
  databaseId: z.string(),
});

export type DatabaseScheduleNotFoundError = z.infer<
  typeof DatabaseScheduleNotFoundError
>;

export const MergeApiPassthroughBadStatusCodeError = BaseErrorSchema.extend({
  errorType: z.literal('MergeApiPassthroughBadStatusCodeError'),
  status: z.number(),
  errorReason: z.string(),
});

export type MergeApiPassthroughBadStatusCodeError = z.infer<
  typeof MergeApiPassthroughBadStatusCodeError
>;

export const isMergeApiPassthroughBadStatusCodeError = (
  error: Record<string, unknown>,
): error is MergeApiPassthroughBadStatusCodeError => {
  return (
    error.errorType === 'MergeApiPassthroughBadStatusCodeError' &&
    typeof error.status === 'number' &&
    typeof error.errorReason === 'string'
  );
};

export const MergeApiPassthroughError = z.union([
  MergeApiPassthroughBadStatusCodeError,
  UnknownError,
]);

export type MergeApiPassthroughError = z.infer<typeof MergeApiPassthroughError>;

export const MigrationsError = BaseErrorSchema.extend({
  errorType: z.literal('MigrationsError'),
  errorReason: z.string(),
});

export type MigrationsError = z.infer<typeof MigrationsError>;

export const MergeDoPassthroughError = z.union([
  MergeApiPassthroughBadStatusCodeError,
  MergeErrorFromLibrary,
  UnknownError,
  ZodParseError,
]);

export type MergeDoPassthroughError = z.infer<typeof MergeDoPassthroughError>;

export const BaseError = z.record(z.string(), z.unknown());
export type BaseError = Record<string, unknown>;
